import { Stack, Text, useTheme } from '@ltvco/refresh-lib/theme';
import { AutoAwesomeOutlined } from '@mui/icons-material';
import React from 'react';

import { MarketValueForm } from '../../components/MarketValueForm';
import { AnalyzingStatus } from '../AnalyzingStatus';
import { useVehicleForecasterSearch } from '../hooks/useVehicleForecasterSearch';

import { VehicleSearchForm } from './VehicleSearchForm';

interface IEnterVehicleDataInstructionProps {
  onSendMessage?: (message?: string, imageBase64?: string) => void;
  hasConversationStarted: boolean;
  shouldResetInputs: boolean;
  onResetComplete: () => void;
  onProcessingStateChange?: (isProcessing: boolean) => void;
  isLoading: boolean;
}

/**
 * EnterVehicleDataInstruction - Shows instruction to enter vehicle data
 * The input fields are only visible when conversation hasn't started yet
 * Sends vehicle search data to the ValueForecaster bot
 * @component
 */
export const EnterVehicleDataInstruction: React.FC<
  IEnterVehicleDataInstructionProps
> = ({
  hasConversationStarted,
  onSendMessage,
  shouldResetInputs = false,
  onResetComplete,
  onProcessingStateChange,
  isLoading,
}) => {
  const {
    searchValue,
    mileage,
    marketValue,
    error,
    hasSubmitted,
    hasSubmittedMarketValue,
    searchType,
    showMarketValueInput,
    handleMileageChange,
    handleSearchValueChange,
    handleMarketValueChange,
    handleSubmit,
    handleMarketValueSubmit,
  } = useVehicleForecasterSearch({
    onSendMessage,
    shouldResetInputs,
    onResetComplete,
    onProcessingStateChange,
  });
  const theme = useTheme();

  const searchLabel = searchType || 'Search';

  // Show form when:
  // 1. Initial state (nothing submitted)
  // 2. OR when market value input is needed (after vehicle search but before final submit)
  const hasSubmittedAny = hasSubmitted || hasSubmittedMarketValue;
  const showForm =
    !isLoading &&
    (showMarketValueInput || (!hasSubmittedAny && !hasConversationStarted));
  const showAnalyzingStatus =
    (hasSubmittedAny || hasConversationStarted) && !showMarketValueInput;

  return (
    <Stack width="100%" justifyContent="center" alignItems="center">
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        p={2}
        mb={2}
        sx={{ backgroundColor: 'background.default', borderRadius: 2 }}
      >
        <AutoAwesomeOutlined sx={{ color: theme.palette.primary.main }} />
        <Text variant="body1">
          Hi! I&apos;m your AI vehicle value forecasting assistant. Enter your
          vehicle information below to get a 3-year depreciation forecast.
        </Text>
      </Stack>
      <Stack width="100%" maxWidth={800}>
        {showForm && (
          <Stack
            direction="column"
            spacing={1}
            width={{ xs: '100%', md: 'fit-content' }}
            maxWidth="760px"
            p={2.5}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            {showMarketValueInput ? (
              <MarketValueForm
                marketValue={marketValue}
                error={error}
                onMarketValueChange={handleMarketValueChange}
                onSubmit={handleMarketValueSubmit}
              />
            ) : (
              <VehicleSearchForm
                searchValue={searchValue}
                mileage={mileage}
                error={error}
                isLoading={isLoading}
                onSearchValueChange={handleSearchValueChange}
                onMileageChange={handleMileageChange}
                onSubmit={handleSubmit}
              />
            )}
          </Stack>
        )}
        {showAnalyzingStatus && (
          <AnalyzingStatus
            searchLabel={searchLabel}
            searchValue={searchValue}
            mileage={mileage}
          />
        )}
      </Stack>
    </Stack>
  );
};
